import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Package, Star } from 'lucide-react';
import { productApi } from '../lib/api';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

export default function RecommendationList({ productId, type = 'product', title = 'Recommended for You' }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchRecommendations = async () => {
      setLoading(true);
      try {
        let response;
        if (type === 'personal') {
          response = await productApi.getPersonalRecommendations();
        } else {
          response = await productApi.getRecommendations(productId);
        }
        setProducts(response.data);
      } catch (error) {
        console.error('Failed to fetch recommendations:', error);
      } finally {
        setLoading(false);
      }
    };

    if (type === 'personal' || productId) {
      fetchRecommendations();
    }
  }, [productId, type]);

  if (!loading && products.length === 0) return null;

  return (
    <div className="mt-20">
      <div className="flex items-center gap-2 mb-8">
        <div className="h-px flex-1 bg-white/10" />
        <h2 className="text-2xl font-semibold tracking-tight uppercase px-4">{title}</h2>
        <div className="h-px flex-1 bg-white/10" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {loading
          ? [...Array(4)].map((_, i) => (
              <div key={i} className="glass-morphic rounded-2xl overflow-hidden h-80 animate-pulse bg-white/5" />
            ))
          : products.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                onClick={() => {
                  navigate(`/products/${product.slug}`);
                  window.scrollTo(0, 0);
                }}
                className="cursor-pointer group"
              >
                <div className="glass-morphic rounded-2xl overflow-hidden hover:border-primary/30 transition-all duration-500 h-full flex flex-col">
                  <div className="aspect-square overflow-hidden bg-muted/20 relative">
                    {product.images && product.images.length > 0 ? (
                      <img
                        src={getImageUrl(product.images[0])}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="h-12 w-12 text-muted-foreground" />
                      </div>
                    )}
                    {product.views > 10 && (
                      <div className="absolute top-2 right-2 bg-primary/20 backdrop-blur-md text-primary-foreground text-[10px] px-2 py-1 rounded-full border border-primary/30">
                        Popular
                      </div>
                    )}
                  </div>
                  <div className="p-4 space-y-2 flex-1 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="font-medium text-foreground group-hover:text-primary transition-colors duration-300 line-clamp-1">
                          {product.name}
                        </h3>
                        <div className="flex items-center gap-1 text-[10px] text-yellow-500">
                          <Star className="h-3 w-3 fill-yellow-500" />
                          {product.average_rating?.toFixed(1) || '0.0'}
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{product.description}</p>
                    </div>
                    <div className="flex items-center justify-between pt-2">
                      <span className="text-lg font-semibold text-primary">₹{product.price.toFixed(2)}</span>
                      <Button size="sm" variant="ghost" className="h-8 rounded-full text-xs">
                        View
                      </Button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
      </div>
    </div>
  );
}
